import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { installAuthStorageSync } from '../utils/authStorageSync.js';
import AppAuth from '../utils/AppAuth.js';
import { setPrivateCacheScope, clearPrivateCacheScope } from '../utils/privateCacheScope.js';
import { setLogoutCallback, setAuthInstance } from '../utils/api.js';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// Fun loading messages for auth steps
const AUTH_STEPS = {
  idle: null,
  authenticating: 'Verifying credentials...',
  syncing: 'Syncing your session...',
  bootstrapping: 'Setting up your workspace...',
  validating: 'Confirming your identity...',
  loading_profile: 'Loading your profile...',
  logging_out: 'Signing you out...',
  clearing_session: 'Clearing session data...'
};

// Alternative quippy messages (randomly selected)
const QUIPPY_STEPS = {
  authenticating: ['Checking the secret handshake...', 'Verifying your credentials...', 'Consulting the oracle...'],
  syncing: ['Syncing your session...', 'Connecting the dots...', 'Aligning the stars...'],
  bootstrapping: ['Setting up your workspace...', 'Preparing your studio...', 'Rolling out the red carpet...'],
  validating: ['Confirming your identity...', 'Double-checking everything...', 'Making sure it\'s really you...'],
  loading_profile: ['Loading your profile...', 'Gathering your photos...', 'Almost there...'],
  logging_out: ['Signing you out...', 'Wrapping things up...', 'See you soon...'],
  clearing_session: ['Clearing session...', 'Tidying up...', 'One moment...']
};

function getStepMessage(step, quippy = false) {
  if (!step || step === 'idle') return null;
  if (quippy && QUIPPY_STEPS[step]) {
    const messages = QUIPPY_STEPS[step];
    return messages[Math.floor(Math.random() * messages.length)];
  }
  return AUTH_STEPS[step] || null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState('idle');
  const [error, setError] = useState(null);
  const [auth] = useState(() => new AppAuth());
  const navigate = useNavigate();

  // Define handleTokenExpired early to avoid hoisting issues
  const handleTokenExpired = useCallback(() => {
    console.log('Token expired, logging out...');
    // Clear local auth state
    auth.logout();
    clearPrivateCacheScope();
    setUser(null);
    setRoles([]);
    setLoading(false);
    // Navigate to login
    navigate('/login');
  }, [auth, navigate]);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    // Set up the logout callback and auth instance for 401 token refresh
    setLogoutCallback(handleTokenExpired);
    setAuthInstance(auth);
  }, [handleTokenExpired]);

  useEffect(() => installAuthStorageSync({
    target: window,
    storage: localStorage,
    auth,
    onIdentityChange: () => {
      // Cookies and localStorage are shared across tabs. Hide the prior identity
      // synchronously, then restart auth so old pending requests cannot restore it.
      clearPrivateCacheScope();
      flushSync(() => {
        setUser(null);
        setRoles([]);
        setLoading(true);
        setLoadingStep('validating');
      });
      window.location.reload();
    }
  }), [auth]);

  async function checkAuth() {
    setLoading(true);
    setError(null);
    try {
      // Check if there's a token first
      if (!auth.isAuthenticated()) {
        setLoading(false);
        setLoadingStep('idle');
        return false;
      }

      setLoadingStep('validating');
      const userData = await auth.validateToken();
      if (userData && userData.valid) {
        setPrivateCacheScope(userData.user?.id, auth.tenantContext?.id || auth.tenantContext?.slug);
        setUser(userData.user);
        setRoles(userData.roles || []);
        if (!auth?.tenantContext?.id) {
          setLoadingStep('bootstrapping');
          await auth.ensureTenantScope();
          setPrivateCacheScope(userData.user?.id, auth.tenantContext?.id || auth.tenantContext?.slug);
        }
        setLoadingStep('loading_profile');
        await refreshUser();
        return true;
      } else {
        // Token invalid, clear it
        auth.logout();
        clearPrivateCacheScope();
        setUser(null);
        setRoles([]);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      // Keep recoverable credentials through Core/backend outages.
      if (!err.retryable) auth.logout();
      setError(err.message || 'Unable to reconnect. Please retry signing in.');
      clearPrivateCacheScope();
      setUser(null);
      setRoles([]);
      return false;
    } finally {
      setLoading(false);
      setLoadingStep('idle');
    }
  }

  async function finalizeCoreSession({ bootstrapName } = {}) {
    const pendingInvite = auth.getPendingInviteToken ? auth.getPendingInviteToken() : null;
    if (pendingInvite) {
      try {
        setLoadingStep('syncing');
        await auth.acceptInvite(pendingInvite);
      } catch (err) {
        console.warn('Failed to accept invite token:', err);
        if (auth.clearPendingInviteToken) {
          auth.clearPendingInviteToken();
        }
      }
    }
    setLoadingStep('bootstrapping');
    await auth.ensureTenantScope({ name: bootstrapName });
    setLoadingStep('validating');
    const userData = await auth.validateToken();
    if (userData && userData.valid) {
      setPrivateCacheScope(userData.user?.id, auth.tenantContext?.id || auth.tenantContext?.slug);
      setUser(userData.user);
      setRoles(userData.roles || []);
    }
    setLoadingStep('loading_profile');
    await refreshUser();
  }

  async function login(email, password) {
    setError(null);
    setLoadingStep('authenticating');
    try {
      const result = await auth.login(email, password);
      // Check if user must change their password before proceeding
      if (result.user?.must_change_password) {
        setLoadingStep('idle');
        return { mustChangePassword: true };
      }
      setPrivateCacheScope(result.user?.id, auth.tenantContext?.id || auth.tenantContext?.slug);
      setUser(result.user);
      await finalizeCoreSession();
      setLoadingStep('idle');
      return true;
    } catch (err) {
      // Check if passkey authentication is required
      if (err.code === 'PASSKEY_REQUIRED') {
        setError('You have passkeys registered. Please use your passkey to sign in.');
        setLoadingStep('idle');
        return { passkeyRequired: true, email: err.email };
      }
      setError(err.message);
      setLoadingStep('idle');
      return false;
    }
  }

  async function forceChangePassword(newPassword) {
    setError(null);
    setLoadingStep('authenticating');
    try {
      await auth.forceChangePassword(newPassword);
      // Now complete the session normally
      const tokenData = auth.validateToken ? await auth.validateToken() : null;
      if (tokenData?.valid) {
        setPrivateCacheScope(tokenData.user?.id, auth.tenantContext?.id || auth.tenantContext?.slug);
        setUser(tokenData.user);
        setRoles(tokenData.roles || []);
      }
      await finalizeCoreSession();
      setLoadingStep('idle');
      return true;
    } catch (err) {
      setError(err.message);
      setLoadingStep('idle');
      return false;
    }
  }

  async function signup({ email, password, name, plan, workspaceName, createWorkspace }) {
    setError(null);
    setLoadingStep('authenticating');
    try {
      const result = await auth.register({ email, password, name, plan });
      const pendingInvite = auth.getPendingInviteToken ? auth.getPendingInviteToken() : null;
      const bootstrapName = createWorkspace && !pendingInvite
        ? (workspaceName || `${name || 'Workspace'}`)
        : null;
      await finalizeCoreSession({ bootstrapName });
      setLoadingStep('idle');
      return true;
    } catch (err) {
      setError(err.message);
      setLoadingStep('idle');
      return false;
    }
  }

  async function logout() {
    if (!window.dispatchEvent(new Event('photos:before-signout', {cancelable: true}))) return;
    // Abort account-scoped uploads before the session credentials are revoked.
    clearPrivateCacheScope();
    setLoadingStep('logging_out');
    try {
      // Try to logout on server
      await fetch(`${auth.baseUrl}/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
    } catch (error) {
      // Ignore logout errors
    }
    setLoadingStep('clearing_session');
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      // Ignore backend logout errors
    }

    auth.logout();
    clearPrivateCacheScope();
    setUser(null);
    setRoles([]);
    setLoadingStep('idle');
    // Navigate to login after logout
    navigate('/login');
  }

  // handleTokenExpired is already defined above

  async function loginWithPasskey(email) {
    setError(null);
    setLoadingStep('authenticating');
    try {
      const result = await auth.loginWithPasskey(email);
      setPrivateCacheScope(result.user?.id, auth.tenantContext?.id || auth.tenantContext?.slug);
      setUser(result.user);
      await finalizeCoreSession();
      setLoadingStep('idle');
      return true;
    } catch (err) {
      setError(err.message);
      setLoadingStep('idle');
      return false;
    }
  }

  async function registerPasskey() {
    setError(null);
    try {
      const result = await auth.registerPasskey();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Refresh user data from local API (for username changes, etc.)
  async function refreshUser() {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const localUser = await res.json();
        // Merge local user data with existing user
        setUser(prev => prev ? { ...prev, ...localUser } : localUser);
      }
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  }

  const hasRole = (role) => roles.includes(role);
  const hasAnyRole = (roleList) => roleList.some(role => roles.includes(role));
  const isAdmin = () => hasAnyRole(['core.admin', 'tenant.admin']);

  const value = {
    user,
    roles,
    loading,
    loadingStep,
    loadingMessage: getStepMessage(loadingStep, true), // Use quippy messages
    error,
    login,
    logout,
    loginWithPasskey,
    signup,
    forceChangePassword,
    registerPasskey,
    checkAuth,
    refreshUser,
    hasRole,
    hasAnyRole,
    isAdmin,
    isAuthenticated: !!user,
    isAuthenticating: loadingStep !== 'idle' && loadingStep !== null,
    auth  // Expose auth client for making authenticated requests
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
